import { useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'hs-lodash/isEqual';
import omit from 'transmute/omit';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { useStore } from 'framework-listing-lib/internal/store/zustand';

import { UI_MODE } from 'framework-listing-lib/constants/uiMode';
import useCurrentView from 'framework-listing-lib/hooks/useCurrentView';
import { updateHiddenFilterForCalendarDisplayDate } from 'framework-listing-lib/internal/Calendar/utils/filters';
import { updateHiddenFilterForFolder } from 'framework-listing-lib/internal/Folders/utils/filters';
import {
  useCurrentViewId,
  useHasViews,
} from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import {
  updateFilterGroupsForBUNav,
  updateHiddenFilterForBUNav,
  updateQuickFiltersForBUNav,
} from 'framework-listing-lib/internal/businessUnits/utils';
import useHandleQueryParams from 'framework-listing-lib/internal/deepLink/hooks/useHandleQueryParams';
import { useHasBUNavIntegration } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useDeepEqual from 'framework-listing-lib/internal/hooks/useDeepEqual';
import { useEventCallback } from 'framework-listing-lib/internal/hooks/useEventCallback';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { createFilterStore, FilterStore } from '../store/filterStateStore';
import {
  CalendarProps,
  FiltersState,
  FrameworkListingLibProviderProps,
} from 'framework-listing-lib/types';
import {
  areSameFilterGroups,
  getAggregatedFilterGroups,
  removeFilterGroups,
} from 'framework-listing-lib/utils/filters';
import useSyncFiltersWithSessionStorage from './useSyncFiltersWithSessionStorage';
import { useCurrentUIMode } from './useUIMode';
import usePrevious from 'react-utils/hooks/usePrevious';
import { getViewId } from '../ViewTabs/utils/views';

/**
 * This hook is meant to be used only by CrmObjectTypeListingClientProvider.
 * It initialises the global filter state used by all hooks & components in this codebase.
 *
 * We update filters from this hook in two scenarios:
 *  - when @initialFilter or @objectType prop changes.
 *  - when the current view changes.
 *
 * This hooks has & controls:
 *  - list of deleted object ids (due to the delay with the CRM, we manually omit deleted objects when sending request to crm-search).
 *  - filters that have been applied to the UI
 */
export default function useCrmObjectTypeListingFilterState({
  calendarProps,
  hiddenFilter: hiddenFilterProp,
  initialFilter: initialFilterProp,
  folderId,
}: {
  calendarProps?: Omit<
    CalendarProps,
    'onDisplayDateChange' | 'onViewTypeChange'
  >;
  hiddenFilter?: FrameworkListingLibProviderProps['hiddenFilter'];
  initialFilter?: FrameworkListingLibProviderProps['initialFilter'];
  folderId?: string | number | undefined;
}) {
  'use memo';
  const urlParams = useHandleQueryParams();
  const currentUIMode = useCurrentUIMode();
  const hasViews = useHasViews();
  const currentView = useCurrentView();
  const currentViewId = useCurrentViewId();
  const initialFilter = useDeepEqual(initialFilterProp);
  const hasBUNavIntegration = useHasBUNavIntegration();
  const objectType = useObjectTypeId();

  const [filtersStateStore] = useState<FilterStore>(() => {
    const store = createFilterStore();
    store.getState().initializeFiltersState({
      currentView,
      uiMode: currentUIMode,
      initialFilter,
    });
    return store;
  });

  const filterStateFromSessionStorage = useSyncFiltersWithSessionStorage({
    filterGroups: useStore(
      filtersStateStore,
      (s) => s.filtersState.filterGroups
    ),
    quickFilters: useStore(
      filtersStateStore,
      (s) => s.filtersState.quickFilters
    ),
    skip: !useStore(filtersStateStore, (s) => s.shouldSyncWithSessionStorage),
  });

  /**
   * Reset filters to its initial state when switching to a non-view UIMode like FOLDERS,
   * or when switching views
   */
  const [prevViewId, setPrevViewId] = useState<string | undefined>(undefined);
  useEffect(() => {
    //set currentViewId to undefined when in folders mode
    if (currentUIMode === UI_MODE.FOLDERS || !currentView) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPrevViewId(undefined);
      return;
    }

    const viewId = getViewId(currentView);

    /**
     * This effect should run only when the current view changes to prevent listing-lib
     * from wiping the entire filters when (for example) only the query changes.
     */
    if (prevViewId === viewId) {
      return;
    }

    setPrevViewId(viewId);

    const resolvedView =
      !currentView || !currentViewId || objectType !== currentView.objectTypeId
        ? undefined
        : {
            ...currentView,
            filterGroups:
              filterStateFromSessionStorage?.filterGroups ??
              currentView?.filterGroups,
            quickFilters:
              filterStateFromSessionStorage?.quickFilters ??
              currentView?.quickFilters,
          };

    filtersStateStore.getState().initializeFiltersState({
      currentView: resolvedView,
      uiMode: currentUIMode,
      initialFilter,
    });
  }, [
    currentUIMode,
    currentView,
    currentViewId,
    filterStateFromSessionStorage?.filterGroups,
    filterStateFromSessionStorage?.quickFilters,
    filtersStateStore,
    initialFilter,
    objectType,
    prevViewId,
  ]);

  const prevUIMode = usePrevious(currentUIMode);
  useEffect(() => {
    if (prevUIMode !== currentUIMode && currentUIMode === UI_MODE.FOLDERS) {
      filtersStateStore.getState().initializeFiltersState({
        currentView: undefined,
        uiMode: currentUIMode,
        initialFilter,
      });
    }
  }, [currentUIMode, filtersStateStore, initialFilter, prevUIMode]);

  /**
   * Reset the current page when switching folders, so we don't carry over an
   * offset from a folder with more pages onto a folder with fewer results
   */
  const prevFolderIdRef = useRef(folderId);
  useEffect(() => {
    if (prevFolderIdRef.current === folderId) {
      return;
    }

    prevFolderIdRef.current = folderId;
    filtersStateStore.getState().resetPaginationOffset();
  }, [folderId, filtersStateStore]);

  /**
   * Merge the current filters into initialFilters when @initialFilters has changed
   * Update @query directly if only the @initialFilters @query field has changed
   */
  const handleInitialFilterChange = useEventCallback(
    (effectEvent: { initialFilter: Partial<FiltersState> | undefined }) => {
      filtersStateStore.getState().updateInitialFilters({
        initialFilter: effectEvent.initialFilter,
        uiMode: currentUIMode,
      });
    }
  );
  const handleQueryChange = useEventCallback(
    (effectEvent: { query: string }) => {
      filtersStateStore.getState().setQuery(effectEvent.query);
    }
  );
  const initialFilterRef = useRef<Partial<FiltersState> | undefined>(
    initialFilter
  );
  useEffect(() => {
    const hasInitialFilter = Boolean(initialFilterRef.current && initialFilter);
    const nonQueryFiltersAreEqual = isEqual(
      omit(['query'], initialFilterRef.current || {}),
      omit(['query'], initialFilter || {})
    );
    const queryIsDifferent = Boolean(
      initialFilterRef.current &&
        initialFilter &&
        initialFilterRef.current.query !== initialFilter.query
    );
    const hasOnlyQueryChanged =
      hasInitialFilter && nonQueryFiltersAreEqual && queryIsDifferent;

    if (hasOnlyQueryChanged) {
      handleQueryChange({ query: initialFilter?.query ?? '' });
    } else if (!nonQueryFiltersAreEqual) {
      handleInitialFilterChange({
        initialFilter,
      });
    }
    initialFilterRef.current = initialFilter;
  }, [handleInitialFilterChange, handleQueryChange, initialFilter]);

  const handleConsumeQueryParams = useEventCallback(
    (effectEvent: { urlParams: NonNullable<typeof urlParams> }) => {
      filtersStateStore.getState().consumeQueryParams({
        urlParams: effectEvent.urlParams,
        currentView,
        uiMode: currentUIMode,
        initialFilter,
      });
    }
  );
  useEffect(() => {
    const isLoadingOrInvalidView = hasViews && !currentView;
    if (
      isLoadingOrInvalidView ||
      !urlParams ||
      filtersStateStore.getState().hasUsedQueryParams
    ) {
      return;
    }
    handleConsumeQueryParams({ urlParams });
  }, [
    currentView,
    filtersStateStore,
    handleConsumeQueryParams,
    hasViews,
    urlParams,
  ]);

  const filterGroups = useStoreWithEqualityFn(
    filtersStateStore,
    (state) => {
      const filterGroupsWithoutHiddenFilter = removeFilterGroups(
        state.filtersState.filterGroups,
        hiddenFilterProp?.filterGroups || []
      );

      const filterGroupsWithBUNav = updateFilterGroupsForBUNav({
        filterGroups: filterGroupsWithoutHiddenFilter,
        hasBUNavIntegration,
      });

      return filterGroupsWithBUNav;
    },
    shallow
  );

  const quickFilters = useStoreWithEqualityFn(
    filtersStateStore,
    (state) =>
      updateQuickFiltersForBUNav({
        quickFilters: state.filtersState.quickFilters,
        hasBUNavIntegration,
      }),
    shallow
  );

  const hiddenFilter = useMemo(() => {
    const withHiddenBUFilter = updateHiddenFilterForBUNav({
      hiddenFilter: hiddenFilterProp,
      hasBUNavIntegration,
    });

    const withHiddenFolderFilter = updateHiddenFilterForFolder({
      folderId,
      hiddenFilter: withHiddenBUFilter,
      isFoldersMode: currentUIMode === UI_MODE.FOLDERS,
    });
    if (!calendarProps || currentUIMode !== UI_MODE.CALENDAR) {
      return withHiddenFolderFilter;
    }
    return updateHiddenFilterForCalendarDisplayDate({
      displayTimestamp: calendarProps.displayTimestamp,
      endPropertyName: calendarProps.endPropertyName,
      hiddenFilter: withHiddenFolderFilter,
      startPropertyName: calendarProps.startPropertyName,
      viewType: calendarProps.use,
    });
  }, [
    calendarProps,
    currentUIMode,
    hasBUNavIntegration,
    hiddenFilterProp,
    folderId,
  ]);

  const deletedObjects = useStore(
    filtersStateStore,
    (state) => state.deletedObjects
  );

  const isFilterFromCustomView = useMemo(() => {
    if (!currentView) {
      return false;
    }

    // eslint-disable-next-line react-hooks/purity
    const now = Date.now();
    const getNow = () => now;

    const currentAggregatedFilterGroups = getAggregatedFilterGroups({
      deletedObjects,
      filterGroups,
      hiddenFilter,
      includeHiddenFilter: true,
      quickFilters,
      getNow,
    });

    const viewAggregatedFilterGroups = getAggregatedFilterGroups({
      deletedObjects,
      filterGroups: updateFilterGroupsForBUNav({
        filterGroups: currentView.filterGroups,
        hasBUNavIntegration,
      }),
      hiddenFilter,
      includeHiddenFilter: true,
      quickFilters: updateQuickFiltersForBUNav({
        quickFilters: currentView.quickFilters,
        hasBUNavIntegration,
      }),
      getNow,
    });

    return areSameFilterGroups(
      currentAggregatedFilterGroups,
      viewAggregatedFilterGroups
    );
  }, [
    currentView,
    deletedObjects,
    filterGroups,
    hasBUNavIntegration,
    hiddenFilter,
    quickFilters,
  ]);

  const value = useMemo(
    () => ({
      filterGroups,
      filtersStateStore,
      hiddenFilter,
      isFilterFromCustomView,
      quickFilters,
    }),
    [
      filterGroups,
      filtersStateStore,
      hiddenFilter,
      isFilterFromCustomView,
      quickFilters,
    ]
  );

  return value;
}
